import { useEffect } from 'react';
import { useTonConnectUI, useTonWallet, CHAIN } from '@tonconnect/ui-react';
import { Address } from '@ton/core';
import { updateCurrentUserWallet } from 'shared/api';
import { useGlobalStore } from 'shared/store';
import { useAlert } from 'shared/ui/components/alert/alert';
import { useTranslation } from 'react-i18next';

/**
 * Main wallet connection hook that handles backend synchronization.
 * 
 * IMPORTANT: This hook should be used sparingly to avoid duplicate API calls.
 * Currently used in:
 * 1. WithdrawAmountModal (always rendered in AppHeader for MainLayout screens)
 * 2. SettingsModule (which uses FullHeightLayout without AppHeader)
 * 
 * For other components that just need to open wallet modal without handling
 * the connection logic, use useWalletConnectModal instead.
 * 
 * Note: When navigating between Settings and other screens, listeners may briefly
 * overlap, but this is acceptable as the wallet state update is idempotent.
 */
export const useWalletConnect = () => {
  const [tonConnectUI] = useTonConnectUI();
  const wallet = useTonWallet();
  const updateStoreUser = useGlobalStore((state) => state.updateUser);
  const { showAlert } = useAlert();
  const { t } = useTranslation();

  // Handle wallet connection/disconnection
  const handleWalletAction = async () => {
    if (wallet) {
      await tonConnectUI.disconnect();
    } else {
      await tonConnectUI.openModal();
    }
  };

  // Listen for wallet status changes and update backend
  useEffect(() => {
    const unsubscribe = tonConnectUI.onStatusChange(async (walletInfo: any) => {
      // Only process if wallet is connected (not null/undefined)
      if (walletInfo && walletInfo.account) {
        try {
          // Format address in user-friendly format (non-bounceable, URL-safe)
          const addr = Address.parse(walletInfo.account.address);
          const isTestnet = walletInfo.account.chain === CHAIN.TESTNET;
          const userFriendlyAddress = addr.toString({
            testOnly: isTestnet,
            bounceable: false,
            urlSafe: true,
          });

          // Update wallet on backend
          const response = await updateCurrentUserWallet({ wallet: userFriendlyAddress });

          // Update local user state
          updateStoreUser({ wallet: response.data.wallet });
        } catch (error: any) {
          console.error('Failed to update wallet on backend:', error);

          // Disconnect wallet on frontend since backend rejected it
          await tonConnectUI.disconnect();

          // Show error alert with backend message if available
          let errorMessage = t('settings.wallet_connection_failed');

          // Check if it's an axios error with response data
          if (error?.response?.data?.detail) {
            errorMessage = error.response.data.detail;
          } else if (error?.message) {
            errorMessage = error.message;
          }

          showAlert(errorMessage);
        }
      }
    });

    // Cleanup subscription
    return () => {
      unsubscribe();
    };
  }, [tonConnectUI]);

  return {
    wallet,
    handleWalletAction,
  };
};
